import {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import type {ApexOptions} from 'apexcharts'
import {ApexChart, useChartTokens, useSegmentNavigation} from '@/components/ui/apex-chart'
import {ListEmptyState, SectionCard, StatCard} from '@/components/section-card'
import {HelpIcon} from '@/components/help-icon'
import {FUENTES_PLAN, type FuentePlan, type PlanResumen} from '@/hooks/sgsst/plan/use-actions'
import {formatShortMonth} from '@/lib/risk'
import {cn} from '@/lib/utils'

/** Excluyentes: una acción cae en exactamente una, así la barra apilada no la cuenta dos veces. */
const SERIES = ['cumplidas', 'vencidas', 'alDia'] as const
type Serie = (typeof SERIES)[number]

/** Memoizados: Apex redibuja y reanima cada vez que sus opciones cambian de identidad. */
function useSerieColors(): Record<Serie, string> {
  const tokens = useChartTokens()
  return useMemo(
    () => ({
      cumplidas: tokens['chart-planear'],
      vencidas: tokens['tone-red-fg'],
      alDia: tokens['chart-actuar'],
    }),
    [tokens]
  )
}

const EFICACIA_TONE = {
  eficaz: 'tone-green',
  no_eficaz: 'tone-red',
  sin_verificar: 'tone-amber',
} as const

/** Un conteo no tiene medios: la marca fraccionaria del eje queda sin rótulo. */
function soloEnteros(value: string | number): string {
  const n = Number(value)
  return Number.isInteger(n) ? String(n) : ''
}

function pct(parte: number, total: number): number {
  return total === 0 ? 0 : Math.round((parte * 100) / total)
}

function Leyenda({colors}: {colors: Record<Serie, string>}) {
  const {t} = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="plan-resumen-leyenda">
      {SERIES.map((serie) => (
        <span key={serie} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span aria-hidden className="inline-block size-2.5 rounded-[2px]" style={{backgroundColor: colors[serie]}} />
          {t(`plan.resumen.serie.${serie}`)}
        </span>
      ))}
    </div>
  )
}

function useSerieNames(): Record<Serie, string> {
  const {t} = useTranslation()
  return useMemo(
    () => ({
      cumplidas: t('plan.resumen.serie.cumplidas'),
      vencidas: t('plan.resumen.serie.vencidas'),
      alDia: t('plan.resumen.serie.alDia'),
    }),
    [t]
  )
}

function apiladaOptions(
  colors: Record<Serie, string>,
  horizontal: boolean,
  categorias: string[],
  events: NonNullable<ApexOptions['chart']>['events']
): ApexOptions {
  return {
    chart: {stacked: true, events},
    colors: SERIES.map((s) => colors[s]),
    plotOptions: {
      bar: {horizontal, borderRadius: 3, borderRadiusApplication: 'end', barHeight: '62%', columnWidth: '58%'},
    },
    // En horizontal Apex dibuja las categorías sobre el eje Y: un formateador numérico ahí
    // pinta «NaN» en vez del nombre.
    xaxis: horizontal ? {categories: categorias, labels: {formatter: soloEnteros}} : {categories: categorias},
    grid: {xaxis: {lines: {show: horizontal}}, yaxis: {lines: {show: !horizontal}}},
    yaxis: horizontal ? {} : {labels: {formatter: soloEnteros}},
    tooltip: {shared: true, intersect: false, y: {formatter: (value: number) => String(value)}},
    legend: {show: false},
  }
}

/** Horizontal porque los nombres de las fuentes son largos. El clic filtra la lista en el servidor. */
function GraficaFuentes({resumen, onFuente}: {resumen: PlanResumen; onFuente: (fuente: FuentePlan) => void}) {
  const {t} = useTranslation()
  const colors = useSerieColors()
  const nombres = useSerieNames()

  const filas = useMemo(
    () =>
      FUENTES_PLAN.map((fuente) => {
        const f = resumen.fuentes.find((x) => x.fuente === fuente)
        const total = f?.total ?? 0
        return {
          fuente,
          total,
          cumplidas: f?.cumplidas ?? 0,
          vencidas: f?.vencidas ?? 0,
          alDia: total - (f?.cumplidas ?? 0) - (f?.vencidas ?? 0),
        }
      }).filter((fila) => fila.total > 0),
    [resumen]
  )

  const claves = useMemo(() => filas.map((f) => f.fuente), [filas])
  const dataPointSelection = useSegmentNavigation(claves, onFuente)
  const options = useMemo<ApexOptions>(
    () =>
      apiladaOptions(
        colors,
        true,
        filas.map((f) => t(`plan.resumen.fuente.${f.fuente}`)),
        {dataPointSelection}
      ),
    [colors, filas, t, dataPointSelection]
  )
  const series = useMemo(
    () => SERIES.map((serie) => ({name: nombres[serie], data: filas.map((f) => f[serie])})),
    [filas, nombres]
  )

  if (filas.length === 0) return null

  return (
    <SectionCard title={t('plan.resumen.fuentes.title')} testId="plan-resumen-fuentes">
      <div className="space-y-3">
        <Leyenda colors={colors} />
        <ApexChart
          type="bar"
          height={Math.max(150, filas.length * 46)}
          options={options}
          series={series}
          clickable
          testId="plan-resumen-fuentes-chart"
        />
      </div>
    </SectionCard>
  )
}

function GraficaMeses({resumen, onMes}: {resumen: PlanResumen; onMes: (mes: number) => void}) {
  const {t} = useTranslation()
  const colors = useSerieColors()
  const nombres = useSerieNames()

  const filas = useMemo(
    () =>
      resumen.meses.map((mes) => ({
        mes: mes.mes,
        cumplidas: mes.cumplidas,
        vencidas: mes.vencidas,
        alDia: mes.a_tiempo,
      })),
    [resumen]
  )
  const claves = useMemo(() => filas.map((f) => f.mes), [filas])
  const dataPointSelection = useSegmentNavigation(claves, onMes)
  const options = useMemo<ApexOptions>(
    () =>
      apiladaOptions(
        colors,
        false,
        filas.map((f) => formatShortMonth(resumen.period_year, f.mes)),
        {dataPointSelection}
      ),
    [colors, filas, resumen.period_year, dataPointSelection]
  )
  const series = useMemo(
    () => SERIES.map((serie) => ({name: nombres[serie], data: filas.map((f) => f[serie])})),
    [filas, nombres]
  )
  const hayAlgo = filas.some((m) => m.cumplidas + m.vencidas + m.alDia > 0)

  return (
    <SectionCard title={t('plan.resumen.meses.title', {year: resumen.period_year})} testId="plan-resumen-meses">
      {hayAlgo ? (
        <div className="space-y-3">
          <Leyenda colors={colors} />
          <div className="overflow-x-auto">
            <ApexChart
              type="bar"
              height={200}
              className="min-w-[380px]"
              options={options}
              series={series}
              clickable
              testId="plan-resumen-meses-chart"
            />
          </div>
          {resumen.fuera_del_anio > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="plan-resumen-fuera-del-anio">
              {t('plan.resumen.meses.fueraDelAnio', {count: resumen.fuera_del_anio})}
            </p>
          )}
        </div>
      ) : (
        <ListEmptyState title={t('plan.resumen.meses.vacio')} />
      )}
    </SectionCard>
  )
}

/** Fichas y no gráfica navegable: la lista del plan no se filtra por eficacia. */
function BloqueEficacia({resumen}: {resumen: PlanResumen}) {
  const {t} = useTranslation()
  const {eficaz, no_eficaz: noEficaz, sin_verificar: sinVerificar} = resumen.eficacia
  return (
    <SectionCard title={t('plan.resumen.eficacia.title')} testId="plan-resumen-eficacia">
      {resumen.cumplidas === 0 ? (
        <ListEmptyState title={t('plan.resumen.eficacia.vacio')} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {(
            [
              ['eficaz', eficaz],
              ['no_eficaz', noEficaz],
              ['sin_verificar', sinVerificar],
            ] as const
          ).map(([clave, valor]) => (
            <div
              key={clave}
              className={cn(EFICACIA_TONE[clave], 'rounded-lg p-4')}
              data-testid={`plan-resumen-eficacia-${clave}`}>
              <p className="font-mono text-[10px] font-semibold tracking-wider uppercase">
                {t(`plan.resumen.eficacia.${clave}`)}
              </p>
              <p className="mt-1 font-heading text-2xl">{valor}</p>
              <p className="text-xs">
                {t('plan.resumen.eficacia.deLasCumplidas', {pct: pct(valor, resumen.cumplidas)})}
              </p>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

/** Sale de un GET agregado en el servidor, nunca de contar la página que trajo la lista. */
export function PlanResumenGerencial({
  resumen,
  businessId,
  planYear,
  onFiltrar,
}: {
  resumen: PlanResumen | undefined
  businessId: string
  planYear: number
  onFiltrar: (filtro: {fuente?: FuentePlan; mes?: number}) => void
}) {
  const {t} = useTranslation()
  const onFuente = useCallback((fuente: FuentePlan) => onFiltrar({fuente}), [onFiltrar])
  const onMes = useCallback((mes: number) => onFiltrar({mes}), [onFiltrar])

  if (!resumen) return null

  if (resumen.total === 0) {
    return (
      <div className="surface mx-auto max-w-2xl" data-testid="plan-resumen-vacio">
        <ListEmptyState page title={t('plan.resumen.vacio.title', {year: planYear})} />
      </div>
    )
  }

  const verificadas = resumen.eficacia.eficaz + resumen.eficacia.no_eficaz
  // Mismo criterio con el que `GraficaFuentes` se retira.
  const hayFuentes = resumen.fuentes.some((f) => f.total > 0)
  const versLista = `/businesses/${businessId}/sgsst/plan`

  return (
    <div className="space-y-4" data-testid="plan-resumen-gerencial">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t('plan.resumen.stats.total')} value={resumen.total} testId="plan-resumen-total" />
        <StatCard
          label={t('plan.resumen.stats.avance')}
          value={`${resumen.cumplidas} / ${resumen.total}`}
          pct={pct(resumen.cumplidas, resumen.total)}
          detail={t('plan.resumen.stats.avanceDetalle', {pct: pct(resumen.cumplidas, resumen.total)})}
          testId="plan-resumen-avance"
        />
        <StatCard
          label={t('plan.resumen.stats.vencidas')}
          value={resumen.vencidas}
          alert={resumen.vencidas > 0}
          detail={
            resumen.vencidas > 0 ? (
              <Link
                to={versLista}
                className="font-medium text-primary hover:underline"
                data-testid="plan-resumen-ver-vencidas">
                {t('plan.resumen.stats.verVencidas')}
              </Link>
            ) : (
              t('plan.resumen.stats.sinVencidas')
            )
          }
          testId="plan-resumen-vencidas"
        />
        <StatCard
          label={t('plan.resumen.stats.porVencer')}
          value={resumen.por_vencer}
          detail={t('plan.resumen.stats.porVencerDetalle')}
          testId="plan-resumen-por-vencer"
        />
      </div>

      <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {t('plan.resumen.corte', {fecha: resumen.hoy})}
        <HelpIcon label={t('plan.resumen.corteAyudaLabel')} testId="plan-resumen-corte-help">
          {t('plan.resumen.corteHelp')}
        </HelpIcon>
        {verificadas > 0 && <span>· {t('plan.resumen.verificadas', {count: verificadas})}</span>}
      </p>

      {/* `[&>*]:min-w-0`: una celda de grilla no baja de su ancho mínimo y la gráfica desbordaría. */}
      <div className={cn('grid gap-4 [&>*]:min-w-0', hayFuentes && 'lg:grid-cols-2')}>
        <GraficaFuentes resumen={resumen} onFuente={onFuente} />
        <GraficaMeses resumen={resumen} onMes={onMes} />
      </div>
      <BloqueEficacia resumen={resumen} />
    </div>
  )
}
